import type { CatDefinition, SongMeta } from '@/types';
import { renderSynthTrack, synthesizeCoinCelebration, synthesizeMeow, synthesizeSplat, synthesizeUnlock } from '@/utils/audioSynth';

/**
 * Owns the single Web Audio graph for the game and is the source of truth for
 * "song time". Gameplay timing (RhythmEngine) reads `getCurrentTime()` rather than
 * accumulating its own delta-time clock, which is what keeps notes drift-free even
 * under frame hitches: the audio hardware clock (AudioContext.currentTime) never
 * skips or slows down the way requestAnimationFrame can.
 */
export class AudioManager {
  private ctx: AudioContext;
  private masterGain: GainNode;
  private musicGain: GainNode;
  private sfxGain: GainNode;

  private currentSource: AudioBufferSourceNode | null = null;
  private currentBuffer: AudioBuffer | null = null;
  private startContextTime = 0;
  private startOffset = 0;
  private playing = false;

  private bufferCache = new Map<string, AudioBuffer>();
  private catVocalSamples = new Map<string, AudioBuffer>();

  private autoPauseListeners: Array<() => void> = [];

  constructor() {
    this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.masterGain = this.ctx.createGain();
    this.musicGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.musicGain.connect(this.masterGain);
    this.sfxGain.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.playing) {
        this.pause();
        this.autoPauseListeners.forEach((cb) => cb());
      }
    });
  }

  /** Must be called from within a user gesture handler (tap/click) to satisfy
   * browser autoplay policies before any audio will produce sound. */
  async unlock(): Promise<void> {
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
  }

  get audioContext(): AudioContext {
    return this.ctx;
  }

  /** Returns an unsubscribe function so callers (e.g. a scene that may be recreated
   * on retry) can avoid leaking listeners across restarts. */
  onAutoPause(cb: () => void): () => void {
    this.autoPauseListeners.push(cb);
    return () => {
      this.autoPauseListeners = this.autoPauseListeners.filter((l) => l !== cb);
    };
  }

  async loadSong(song: SongMeta): Promise<AudioBuffer> {
    const cached = this.bufferCache.get(song.id);
    if (cached) return cached;

    let buffer: AudioBuffer;
    if (song.audio === 'synth') {
      buffer = await renderSynthTrack(song.synthTrack ?? 'popLoop', song.bpm, song.duration);
    } else {
      const response = await fetch(song.audio);
      const arrayBuffer = await response.arrayBuffer();
      buffer = await this.ctx.decodeAudioData(arrayBuffer);
    }
    this.bufferCache.set(song.id, buffer);
    return buffer;
  }

  /** Registers a real recorded vocal sample for a cat + pitch combo. Once registered,
   * playCatVocal prefers this over the synthesized fallback -- the whole point being
   * that swapping in real audio later requires no changes to gameplay code. */
  registerCatVocalSample(catId: string, pitch: number, buffer: AudioBuffer): void {
    this.catVocalSamples.set(`${catId}:${pitch}`, buffer);
  }

  /** Loads a song's isolated vocal track (SongMeta.vocalsAudio), if it has one. This
   * is the full original recording, not per-note clips -- playVocalClip plays a slice
   * of it starting at a given note's own timestamp, since chart note times and the
   * vocal track share the same source timeline. */
  async loadVocals(song: SongMeta): Promise<AudioBuffer | null> {
    if (!song.vocalsAudio) return null;
    const cacheKey = `${song.id}:vocals`;
    const cached = this.bufferCache.get(cacheKey);
    if (cached) return cached;

    const response = await fetch(song.vocalsAudio);
    const arrayBuffer = await response.arrayBuffer();
    const buffer = await this.ctx.decodeAudioData(arrayBuffer);
    this.bufferCache.set(cacheKey, buffer);
    return buffer;
  }

  /** Plays [startSec, startSec + durationSec) of a loaded vocal track -- the real sung
   * syllable for a caught note, in place of a synthesized meow. A short fade in/out
   * avoids an audible click at the slice boundaries. */
  playVocalClip(buffer: AudioBuffer, startSec: number, durationSec: number, velocity = 1): void {
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    const gain = this.ctx.createGain();
    const when = this.ctx.currentTime;
    const fade = Math.min(0.015, durationSec / 4);
    gain.gain.setValueAtTime(0, when);
    gain.gain.linearRampToValueAtTime(velocity, when + fade);
    gain.gain.setValueAtTime(velocity, when + Math.max(fade, durationSec - fade));
    gain.gain.linearRampToValueAtTime(0, when + durationSec);
    source.connect(gain);
    gain.connect(this.sfxGain);
    source.start(when, Math.max(0, startSec), durationSec);
  }

  play(buffer: AudioBuffer, offsetSec = 0): void {
    this.stopSourceOnly();
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.musicGain);
    const safeOffset = Math.max(0, Math.min(offsetSec, buffer.duration));
    source.start(0, safeOffset);
    this.currentSource = source;
    this.currentBuffer = buffer;
    this.startContextTime = this.ctx.currentTime;
    this.startOffset = safeOffset;
    this.playing = true;
  }

  pause(): void {
    if (!this.playing) return;
    this.startOffset = this.getCurrentTime();
    this.stopSourceOnly();
    this.playing = false;
  }

  resume(): void {
    if (this.playing || !this.currentBuffer) return;
    this.play(this.currentBuffer, this.startOffset);
  }

  stop(): void {
    this.stopSourceOnly();
    this.playing = false;
    this.startOffset = 0;
  }

  seek(offsetSec: number): void {
    if (this.playing && this.currentBuffer) {
      this.play(this.currentBuffer, offsetSec);
    } else {
      this.startOffset = offsetSec;
    }
  }

  private stopSourceOnly(): void {
    if (this.currentSource) {
      try {
        this.currentSource.onended = null;
        this.currentSource.stop();
      } catch {
        // already stopped
      }
      this.currentSource.disconnect();
      this.currentSource = null;
    }
  }

  isPlaying(): boolean {
    return this.playing;
  }

  getDuration(): number {
    return this.currentBuffer?.duration ?? 0;
  }

  /** The authoritative song-time clock, in seconds. */
  getCurrentTime(): number {
    if (!this.playing) return this.startOffset;
    return this.ctx.currentTime - this.startContextTime + this.startOffset;
  }

  setMusicVolume(v: number): void {
    this.musicGain.gain.value = Math.max(0, Math.min(1, v));
  }

  setSfxVolume(v: number): void {
    this.sfxGain.gain.value = Math.max(0, Math.min(1, v));
  }

  playCatVocal(cat: CatDefinition, pitch: number, velocity = 1): void {
    const sample = this.catVocalSamples.get(`${cat.id}:${pitch}`);
    if (sample) {
      const source = this.ctx.createBufferSource();
      source.buffer = sample;
      const gain = this.ctx.createGain();
      gain.gain.value = velocity;
      source.connect(gain);
      gain.connect(this.sfxGain);
      source.start();
      return;
    }
    synthesizeMeow(this.ctx, this.sfxGain, pitch, this.ctx.currentTime, velocity);
  }

  playSplat(): void {
    synthesizeSplat(this.ctx, this.sfxGain, this.ctx.currentTime);
  }

  playUnlock(): void {
    synthesizeUnlock(this.ctx, this.sfxGain, this.ctx.currentTime);
  }

  playCoinCelebration(): void {
    synthesizeCoinCelebration(this.ctx, this.sfxGain, this.ctx.currentTime);
  }
}
